import type { FinanceInfo } from '@carvana/merch-contracts';
import type { Inventory } from '~/context/srp/InventoryContext';

const TTL_MS = 5 * 60 * 1000;

// Presence means a LoanTermPricing fetch already resolved for this URL;
// financeInfo can still be undefined if pricing errored.
export type CachedFinanceSnapshot = {
  financeInfo: FinanceInfo | undefined;
};

type CacheEntry = {
  cachedAt: number;
  financeSnapshot?: CachedFinanceSnapshot;
  inventory: Inventory;
  key: string;
};

export type CacheReadStatus = 'expired' | 'hit' | 'miss';

// Holds at most one entry, matching the single hop native bfcache covers —
// caching more would gain nothing over the control experience.
let entry: CacheEntry | null = null;

function evaluate(key: string): CacheReadStatus {
  if (!entry || entry.key !== key) return 'miss';

  if (Date.now() - entry.cachedAt > TTL_MS) {
    entry = null;

    return 'expired';
  }

  return 'hit';
}

function readEntry(key: string): {
  inventory: Inventory | null;
  status: CacheReadStatus;
} {
  const status = evaluate(key);

  return { inventory: status === 'hit' ? entry!.inventory : null, status };
}

/**
 * The canonical SRP URL that useSyncUrl embeds in every history entry. Used as
 * the cache key so a back-nav restore reads the same key the forward fetch wrote.
 */
export function getSrpCacheKey(): string | null {
  if (typeof window === 'undefined') return null;

  return window.history.state?.srp ?? null;
}

const inventoryCache = {
  clear() {
    entry = null;
  },
  get(key: string): Inventory | null {
    return readEntry(key).inventory;
  },
  // A finance snapshot only matters on a hit, so a nullable return is enough here.
  getFinanceSnapshot(key: string): CachedFinanceSnapshot | null {
    if (evaluate(key) !== 'hit') return null;

    return entry?.financeSnapshot ?? null;
  },
  has(key: string): boolean {
    return evaluate(key) === 'hit';
  },
  // Like get(), but also reports why a read did not resolve (miss vs expired).
  read(key: string): { inventory: Inventory | null; status: CacheReadStatus } {
    return readEntry(key);
  },
  set(
    key: string,
    inventory: Inventory,
    financeSnapshot?: CachedFinanceSnapshot
  ) {
    entry = { cachedAt: Date.now(), financeSnapshot, inventory, key };
  }
};

export default inventoryCache;
